// Phase 86AZ.60 — Public contractor landing page (/muteahhit)
// ===========================================================
// Mirrors TedarikciLanding.jsx 1:1 in structure but uses a red palette
// and contractor-specific content (anahtar teslim stand, mekanik/elektrik,
// dijital LED, sahne/ses, vb.).
import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight, ShieldCheck, Sparkles, ScanSearch, ChartLine,
  HardHat, Lock, Eye, FileText, Award, Globe, Handshake,
  Building2, ArrowRight, CheckCircle2, Mail, Wrench, Zap, Hammer,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const NAVY = '#0A3A5A';

const SectionEyebrow = ({ children }) => (
  <span className="inline-block text-[11px] uppercase tracking-[0.28em] font-semibold text-red-700 bg-red-50 border border-red-100 px-3 py-1 rounded-full">
    {children}
  </span>
);

const BenefitCard = ({ icon: Icon, title, description, num }) => (
  <div className="group relative bg-white border border-[rgba(10,58,90,0.10)] rounded-2xl p-7 hover:border-red-300 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
    <span className="absolute top-5 right-5 text-[10px] font-mono tracking-[0.2em] text-red-600/60">{num}</span>
    <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center mb-5 group-hover:bg-red-600 group-hover:border-red-600 transition-colors">
      <Icon className="w-5 h-5 text-red-700 group-hover:text-white transition-colors" />
    </div>
    <h3 className="font-playfair text-xl mb-2 leading-tight" style={{ color: NAVY }}>{title}</h3>
    <p className="text-[14px] text-[#4A5568] leading-relaxed">{description}</p>
  </div>
);

const StepItem = ({ num, title, copy, last }) => (
  <div className="relative flex flex-col items-start">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">{num}</div>
      {!last && <div className="hidden md:block h-px w-12 lg:w-20 bg-gradient-to-r from-red-400 to-transparent" />}
    </div>
    <h4 className="font-playfair text-lg leading-snug mb-1.5" style={{ color: NAVY }}>{title}</h4>
    <p className="text-[13.5px] text-[#4A5568] leading-relaxed max-w-xs">{copy}</p>
  </div>
);

const PromiseRow = ({ icon: Icon, title, copy }) => (
  <div className="flex items-start gap-4 py-5 border-b border-white/10 last:border-0">
    <div className="shrink-0 w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center">
      <Icon className="w-4 h-4 text-red-300" />
    </div>
    <div>
      <h4 className="font-semibold text-white mb-1">{title}</h4>
      <p className="text-[13.5px] text-white/70 leading-relaxed">{copy}</p>
    </div>
  </div>
);

export default function MuteahhitLanding() {
  const { language } = useLanguage();
  const tr = language === 'tr';

  const benefits = tr ? [
    { icon: Globe,       t: 'Doğrulanmış Proje Havuzu',  d: 'SummitFlow kapsamında 195+ ülke delegasyonu ve onaylı kurumlardan oluşan, doğrudan bağlı proje siparişlerine erişin.' },
    { icon: ShieldCheck, t: 'Onaylı Alıcı, Net Ödeme',   d: 'Her proje SummitFlow organizasyon komitesi tarafından doğrulanır. Ödeme akışı platform üzerinden, gecikmesiz, izlenebilir biçimde işler.' },
    { icon: ChartLine,   t: 'Açık Teklif Verme',         d: 'Her aday proje için kategori, m², bütçe ve takvim açıkça görünür. Teklifinizi şartları görerek, açık fiyatla gönderirsiniz.' },
    { icon: Hammer,      t: 'Anahtar Teslim Akış',       d: 'Tasarım onayı, malzeme listesi, montaj/demontaj slotları ve İSG (İş Sağlığı & Güvenliği) tek panelden yönetilir.' },
    { icon: Sparkles,    t: 'Portföyünüzü Sergileyin',   d: 'Önceki projeleriniz, sertifikalarınız ve referanslarınızla profesyonel firma sayfası — organizatörler size ulaşır.' },
    { icon: Award,       t: 'Müteahhit Skoru',           d: 'Zamanında teslim, kalite ve müşteri memnuniyetine göre artan skor; bir sonraki etkinlikte öncelikli davet alma hakkı.' },
  ] : [
    { icon: Globe,       t: 'Verified Project Pool',     d: 'Access concrete project orders bound to 195+ country delegations and approved organizations under SummitFlow.' },
    { icon: ShieldCheck, t: 'Approved Buyers, Clean Payments', d: 'Every project is verified by the SummitFlow organizing committee. Payments flow through the platform — no delays, fully auditable.' },
    { icon: ChartLine,   t: 'Open Bidding',              d: 'For each candidate project, the category, m², budget and timeline are visible up-front. You bid with full clarity, at an open price.' },
    { icon: Hammer,      t: 'Turnkey Workflow',          d: 'Design approval, BOM, install/dismantle slots and OHS (Occupational Health & Safety) are all managed in one panel.' },
    { icon: Sparkles,    t: 'Showcase Your Portfolio',   d: 'Publish your past projects, certifications and references on a professional profile — organizers come to you.' },
    { icon: Award,       t: 'Contractor Score',          d: 'A score that grows with on-time delivery, quality and client satisfaction — earn priority invitations at the next event.' },
  ];

  const steps = tr ? [
    { t: 'Başvuru',       c: 'Şirket bilgileri, kategori (anahtar teslim / mekanik / dijital LED vb.), vergi numarası ve banka bilgileri ile kayıt formunu doldurun.' },
    { t: 'Doğrulama',     c: 'Belgeleriniz uzman ekibimiz tarafından 48 saat içinde incelenir; referanslar gerekirse istenir.' },
    { t: 'Aday Projeler', c: 'Onayın ardından kategorinize uygun aday projeleri panelinizden görüp tek tek teklif gönderebilirsiniz.' },
    { t: 'Kazan, Uygula', c: 'Onaylı tekliflerde sözleşme dijital imzayla yapılır; montaj/demontaj ve ödeme akışı platformdan koordine edilir.' },
  ] : [
    { t: 'Apply',         c: 'Submit your company info, category (turnkey / mechanical / digital LED etc.), tax ID and bank details.' },
    { t: 'Verification',  c: 'Our specialists review your documents within 48h and may request references if needed.' },
    { t: 'Candidate Projects', c: 'Once approved, you see candidate projects matching your category in your panel and bid on them individually.' },
    { t: 'Win, Execute',  c: 'Approved bids are digitally signed into contract; install/dismantle and payment flow are coordinated via the platform.' },
  ];

  return (
    <div className="bg-[#F7F5EF] min-h-screen notranslate" translate="no">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: `radial-gradient(60% 80% at 75% 20%, rgba(220,38,38,0.10) 0%, transparent 60%), radial-gradient(40% 50% at 0% 100%, rgba(10,58,90,0.06) 0%, transparent 60%)`
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-28">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <SectionEyebrow>{tr ? 'Müteahhit Programı · 14-16 Ekim 2026' : 'Contractor Program · 14-16 October 2026'}</SectionEyebrow>
              <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl leading-[0.95] mt-6 mb-7" style={{ color: NAVY }}>
                {tr ? (<>Zirvenin<br/><span className="italic text-red-700">resmi yapım</span> ekibine katılın.</>) : (<>Join the official<br/><span className="italic text-red-700">construction</span> team of the summit.</>)}
              </h1>
              <p className="text-[16px] md:text-[17px] text-[#3A4A5C] leading-relaxed max-w-xl mb-10">
                {tr
                  ? 'SummitFlow kapsamında anahtar teslim stand üretimi, mekanik/elektrik tesisat, dijital LED & sahne, sahne dekoru ve özel inşaat işlerinde uzman müteahhit firmalarımız ile çalışıyoruz. Doğrulanmış projeler, açık teklif sistemi ve dijital onay akışı.'
                  : 'For SummitFlow we partner with contractor firms specialized in turnkey stand construction, mechanical/electrical works, digital LED & stages, set design and special builds. Verified projects, open bidding and a digital approval workflow.'}
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/yuklenci-kayit" data-testid="contractor-landing-signup"
                  className="group inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-7 py-4 rounded-full font-semibold shadow-lg shadow-red-600/20 transition-all">
                  {tr ? 'Müteahhit Olmak için Başvur' : 'Apply to Become a Contractor'}
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </Link>
                <Link to="/giris?tab=contractor&only=1" data-testid="contractor-landing-signin"
                  className="inline-flex items-center gap-2 border border-[rgba(10,58,90,0.18)] hover:border-red-500 hover:text-red-700 text-[#0A3A5A] px-7 py-4 rounded-full font-semibold transition-colors bg-white/60 backdrop-blur-sm">
                  {tr ? 'Hesabıma Giriş Yap' : 'Sign In to My Account'}
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-7 gap-y-2 mt-9 text-[12.5px] text-[#5A6B7D]">
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-red-600" />{tr ? 'Üyelik ücretsiz' : 'Free to join'}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-red-600" />{tr ? '48 saatte değerlendirme' : '48-hour review'}</span>
                <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-red-600" />{tr ? 'Açık teklif sistemi' : 'Open bidding'}</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-700 rounded-3xl rotate-3 opacity-20" />
                <div className="relative bg-white border border-[rgba(10,58,90,0.10)] rounded-3xl p-8 shadow-xl">
                  <div className="flex items-center justify-between mb-6">
                    <SectionEyebrow>{tr ? 'Bir Bakışta' : 'At a Glance'}</SectionEyebrow>
                    <HardHat className="w-5 h-5 text-red-600" />
                  </div>
                  <dl className="space-y-5">
                    {[
                      { k: tr ? 'Toplam Stand Alanı'    : 'Total Stand Area',    v: '32,000 m²' },
                      { k: tr ? 'Tahmini Proje Sayısı'  : 'Estimated Projects',  v: '450+' },
                      { k: tr ? 'Etkinlik Süresi'       : 'Event Duration',      v: tr ? '3 gün' : '3 days' },
                      { k: tr ? 'Hazırlık Penceresi'    : 'Build-up Window',     v: tr ? '6 hafta' : '6 weeks' },
                    ].map((row, i) => (
                      <div key={i} className="flex items-baseline justify-between gap-4 py-2 border-b border-[rgba(10,58,90,0.08)] last:border-0">
                        <dt className="text-[13px] text-[#5A6B7D]">{row.k}</dt>
                        <dd className="font-playfair text-2xl font-semibold" style={{ color: NAVY }}>{row.v}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-7 pt-6 border-t border-[rgba(10,58,90,0.08)]">
                    <p className="text-[12px] text-[#5A6B7D] leading-relaxed">
                      {tr ? 'Veriler, SummitFlow organizasyon komitesi tahminlerine ve önceki dönem ortalamalarına dayanmaktadır.' : 'Figures are based on the SummitFlow organizing committee estimates and historical averages.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="bg-white border-y border-[rgba(10,58,90,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-2xl mb-14">
            <SectionEyebrow>{tr ? 'Neden Katılmalı?' : 'Why Join?'}</SectionEyebrow>
            <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5" style={{ color: NAVY }}>
              {tr ? 'Sıradan bir taşeron işi değil — birinci sınıf bir referans.' : 'Not just another subcontract — a first-class reference.'}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (<BenefitCard key={i} num={`0${i+1}`} icon={b.icon} title={b.t} description={b.d} />))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="max-w-2xl mb-14">
          <SectionEyebrow>{tr ? 'Süreç Nasıl İşliyor?' : 'How It Works'}</SectionEyebrow>
          <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5" style={{ color: NAVY }}>
            {tr ? 'Dört basit adımda projede.' : 'On the project in four simple steps.'}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {steps.map((s, i) => (<StepItem key={i} num={i+1} title={s.t} copy={s.c} last={i === steps.length - 1} />))}
        </div>
      </section>

      {/* ── CATEGORIES ── */}
      <section className="bg-white border-y border-[rgba(10,58,90,0.08)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="max-w-2xl mb-14">
            <SectionEyebrow>{tr ? 'Aradığımız Uzmanlık Alanları' : 'Disciplines We Look For'}</SectionEyebrow>
            <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5" style={{ color: NAVY }}>
              {tr ? 'Tek bir alanda uzman? Müthiş — yeriniz hazır.' : 'Specialist in one area? Great — your spot is ready.'}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { i: Hammer,  t: tr ? 'Anahtar Teslim Stand'   : 'Turnkey Stand Build' },
              { i: Wrench,  t: tr ? 'Mekanik / Elektrik'      : 'Mechanical / Electrical' },
              { i: Zap,     t: tr ? 'Dijital LED & Ekran'      : 'Digital LED & Screens' },
              { i: Sparkles,t: tr ? 'Sahne, Ses, Aydınlatma'   : 'Stage, Sound, Lighting' },
              { i: HardHat, t: tr ? 'Özel İnşaat & Pavyon'     : 'Special Build & Pavilion' },
              { i: Building2, t: tr ? 'Modüler Stand Çözümleri' : 'Modular Stand Solutions' },
            ].map((c, i) => (
              <div key={i} className="flex items-center gap-4 bg-[#F7F5EF] border border-[rgba(10,58,90,0.08)] rounded-2xl p-5 hover:border-red-300 transition-colors">
                <div className="w-11 h-11 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center"><c.i className="w-5 h-5 text-red-700" /></div>
                <p className="font-semibold text-[14.5px]" style={{ color: NAVY }}>{c.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRANSPARENCY ── */}
      <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0E2B40 100%)` }}>
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, rgba(220,38,38,0.25) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(220,38,38,0.15) 0%, transparent 40%)`
        }} />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-14 items-start">
            <div className="lg:col-span-5">
              <SectionEyebrow>{tr ? 'Şeffaflık Sözümüz' : 'Transparency Commitment'}</SectionEyebrow>
              <h2 className="font-playfair text-4xl md:text-5xl leading-[1.05] mt-5 text-white">{tr ? 'Karanlık nokta yok.' : 'Zero blind spots.'}</h2>
              <p className="text-[15px] text-white/70 leading-relaxed mt-6 max-w-md">
                {tr ? 'SummitFlow, zirvenin ruhuna uygun olarak müteahhit ekosisteminde tam şeffaflığı kural haline getirir. Aşağıdaki taahhütler tüm müteahhitlerimiz için bağlayıcıdır.' : 'In line with the spirit of the summit, SummitFlow makes full transparency a rule across the contractor ecosystem. The commitments below are binding for every contractor we work with.'}
              </p>
            </div>
            <div className="lg:col-span-7">
              <PromiseRow icon={Eye}        title={tr ? 'Açık Teklif Penceresi' : 'Open Bidding Window'} copy={tr ? 'Her aday projenin teklif penceresi, katılım kriterleri ve değerlendirme yöntemi panelde tek tıkla görüntülenir.' : 'For every candidate project, the bid window, participation criteria and evaluation method are one click away in the panel.'} />
              <PromiseRow icon={FileText}   title={tr ? 'Standart Sözleşme' : 'Standard Contract'} copy={tr ? 'Tüm projelerde aynı sözleşme şablonu kullanılır; gizli madde ya da projeye özel ek yükümlülük çıkmaz.' : 'The same contract template applies to all projects; no hidden clauses or project-specific extra obligations.'} />
              <PromiseRow icon={ScanSearch} title={tr ? 'Denetim İzi' : 'Audit Trail'} copy={tr ? 'Tüm teklif, sözleşme ve ödeme aksiyonları zaman damgalı kayıt altındadır. Hem yöneticiler hem müteahhitler aksiyon geçmişini görür.' : 'Every bid, contract and payment action is time-stamped and logged. Both admins and contractors can inspect the history.'} />
              <PromiseRow icon={Lock}       title={tr ? 'KVKK & GDPR Uyumlu Veri' : 'KVKK & GDPR-Compliant Data'} copy={tr ? 'Kişisel ve şirket verileri en az hak ilkesiyle saklanır; üçüncü taraflarla pazarlama amaçlı paylaşılmaz.' : 'Personal and company data is stored under the principle of least privilege and is never shared with third parties for marketing.'} />
              <PromiseRow icon={Handshake}  title={tr ? 'Çift Yönlü Değerlendirme' : 'Two-Way Rating'} copy={tr ? 'Müşteriler müteahhitleri puanlar; müteahhitler de problemli alıcıları işaretleyebilir. Skor algoritması herkese açıktır.' : 'Customers rate contractors; contractors can flag problematic buyers. The scoring algorithm is publicly documented.'} />
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="relative bg-white border border-[rgba(10,58,90,0.10)] rounded-3xl p-10 md:p-16 overflow-hidden">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-red-100 rounded-full blur-3xl opacity-60 pointer-events-none" />
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <SectionEyebrow>{tr ? 'Hemen Başlayın' : 'Start Now'}</SectionEyebrow>
              <h3 className="font-playfair text-3xl md:text-4xl leading-tight mt-4" style={{ color: NAVY }}>
                {tr ? 'Yer sınırlı — başvurunuz 48 saatte değerlendirilir.' : 'Spaces are limited — applications are reviewed within 48 hours.'}
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/yuklenci-kayit" data-testid="contractor-landing-cta-signup"
                className="group inline-flex items-center justify-between gap-3 bg-red-600 hover:bg-red-700 text-white px-7 py-5 rounded-2xl font-semibold transition-colors">
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center text-sm font-bold">↗</span>
                  {tr ? 'Müteahhit Olarak Kayıt Ol' : 'Sign Up as Contractor'}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/giris?tab=contractor&only=1" data-testid="contractor-landing-cta-signin"
                className="group inline-flex items-center justify-between gap-3 border-2 border-[rgba(10,58,90,0.12)] hover:border-red-500 hover:text-red-700 px-7 py-5 rounded-2xl font-semibold transition-colors" style={{ color: NAVY }}>
                <span className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-sm font-bold text-red-700">→</span>
                  {tr ? 'Mevcut Hesabıma Giriş Yap' : 'Sign In to Existing Account'}
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="mailto:muteahhit@summitflow.net" data-testid="contractor-landing-contact"
                className="inline-flex items-center gap-2 text-[13px] text-[#5A6B7D] hover:text-red-700 mt-2 transition-colors">
                <Mail className="w-3.5 h-3.5" />
                {tr ? 'Sorularınız için muteahhit@summitflow.net' : 'Questions? muteahhit@summitflow.net'}
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[rgba(10,58,90,0.08)] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row gap-3 items-center justify-between text-[12px] text-[#5A6B7D]">
          <p>© 2026 SummitFlow Platform · {tr ? 'Tüm hakları saklıdır.' : 'All rights reserved.'}</p>
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-red-700 transition-colors">{tr ? 'Ana Sayfa' : 'Home'}</Link>
            <Link to="/tedarikci/adaylar" className="hover:text-red-700 transition-colors">{tr ? 'Önerilen Adaylar' : 'Recommended Candidates'}</Link>
            <Link to="/yuklenci-kayit" className="hover:text-red-700 transition-colors">{tr ? 'Başvuru' : 'Apply'}</Link>
            <Link to="/giris?tab=contractor&only=1" className="hover:text-red-700 transition-colors">{tr ? 'Giriş' : 'Sign in'}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
